import React, { useEffect, useRef } from 'react';
import { HslaColor, HslColor } from 'react-colorful';
import { useColorPicker } from 'packages/ds/ColorPicker/components/InternalColorPickerContext';
import { Color, SolidColor } from '../../types';
import { Saturation, type SaturationRef } from './Saturation';
import { HueAndOpacity, type HueAndOpacityRef } from './HueAndOpacity';
import { ChangeColorType } from '../ChangeColorType';
import { hslColorToSolidColor, solidColorToHslColor } from '../../utils/hsl';

type Props = {
    defaultValue: SolidColor;
    onChange: (newValue: Color) => void;
    isColorTypeChangeAllowed?: boolean;
};

export function SolidColorPicker(props: Props) {
    const { isColorDragging } = useColorPicker();
    const color = useRef(toColor(props.defaultValue));
    const saturationRef = useRef<SaturationRef>(null);
    const hueAndOpacityRef = useRef<HueAndOpacityRef>(null);

    function onSaturationChange(value: HslColor) {
        color.current = { ...color.current, ...value };
        const solidColor = hslColorToSolidColor(color.current);

        hueAndOpacityRef.current?.setSaturationLightness(value.s, value.l);
        props.onChange(solidColor);
    }

    function onHueChange(value: HslaColor) {
        color.current = { ...color.current, ...value };
        const solidColor = hslColorToSolidColor(color.current);

        saturationRef.current?.setHueSaturationLightness(value);
        props.onChange(solidColor);
    }

    useEffect(() => {
        color.current = toColor(props.defaultValue);
        saturationRef.current?.setHueSaturationLightness(color.current);
        hueAndOpacityRef.current?.setValue(color.current);
    }, [props.defaultValue]);

    return (
        <>
            <Saturation ref={saturationRef} defaultValue={color.current} onChange={onSaturationChange} />
            {props.isColorTypeChangeAllowed && <ChangeColorType value={props.defaultValue} onChange={props.onChange} />}
            <HueAndOpacity
                ref={hueAndOpacityRef}
                defaultValue={color.current}
                onChange={onHueChange}
                isColorDragging={isColorDragging}
            />
        </>
    );
}

function toColor(value: SolidColor): HslaColor | HslColor {
    const hexColor: string = typeof value === 'string' ? value : value.color;
    const opacity = typeof value === 'string' ? undefined : value.opacity;

    return solidColorToHslColor(hexColor, opacity, undefined);
}
